/**
 * Dispenser (rules v2) machine: a small market vending box on one board cell. A striped awning,
 * a red body, a glass window showing the top item large (the item itself is the board's
 * TileView, so it flies to the bag like any tile) and a "×N" badge for the items still inside.
 * While more than one item is left a blank card peeks out behind the shown one: something
 * follows, but only the top is known. Dimmed like a covered tile while a tile lies over it,
 * with its awning and badge raised to peek out above that tile so the machine still reads;
 * empty, it folds down and is gone. Code-drawn like the glue tape: textures baked once per game.
 */
import * as Phaser from 'phaser';
import { C, FONT, css } from '../game/theme.js';
import { vp } from '../game/viewport.js';
import { deco, dur } from '../fx/motion.js';
import { MACHINE_ART as ART, counterText, itemClip, peekLift } from './dispensers.js';
import type { TileView } from './board.js';

const BODY = 'fx_disp_body';
const AWNING = 'fx_disp_awning';
const GLASS = 'fx_disp_glass';
const CARD = 'fx_disp_card';
const BADGE = 'fx_disp_badge';
const TW = ART.w;
const TH = ART.h;
/** window rectangle in texture px (the item sits in it) */
const WIN = ART.win;
const COVERED_TINT = 0x999999; // same 40% darker as covered tiles (GP-01.1)

function canvas(w: number, h: number): [HTMLCanvasElement, CanvasRenderingContext2D] {
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  return [c, c.getContext('2d')!];
}

function rounded(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function bake(scene: Phaser.Scene): void {
  if (scene.textures.exists(BODY)) return;
  const ink = css(C.ink);
  // body: shadow, red box, awning with scallops, window well, chute
  {
    const [c, ctx] = canvas(TW, TH);
    ctx.lineJoin = 'round';
    rounded(ctx, 12, 30, TW - 24, TH - 38, 22);
    ctx.fillStyle = 'rgba(74,44,42,0.28)';
    ctx.translate(0, 8);
    ctx.fill();
    ctx.translate(0, -8);
    rounded(ctx, 12, 26, TW - 24, TH - 38, 22);
    ctx.fillStyle = css(C.tomato);
    ctx.fill();
    ctx.save();
    ctx.clip();
    ctx.fillStyle = css(C.tomatoDark);
    ctx.fillRect(0, TH - 44, TW, 40);
    ctx.fillStyle = 'rgba(255,255,255,0.18)';
    ctx.fillRect(18, 30, 10, TH - 60);
    ctx.restore();
    ctx.strokeStyle = ink;
    ctx.lineWidth = 6;
    rounded(ctx, 12, 26, TW - 24, TH - 38, 22);
    ctx.stroke();
    // window well (cream frame, dark glass back)
    rounded(ctx, WIN.x - 8, WIN.y - 8, WIN.w + 16, WIN.h + 16, WIN.r + 6);
    ctx.fillStyle = css(C.cream);
    ctx.fill();
    ctx.stroke();
    rounded(ctx, WIN.x, WIN.y, WIN.w, WIN.h, WIN.r);
    const g = ctx.createLinearGradient(0, WIN.y, 0, WIN.y + WIN.h);
    g.addColorStop(0, '#dff4f1');
    g.addColorStop(1, '#a9dcd6');
    ctx.fillStyle = g;
    ctx.fill();
    ctx.lineWidth = 4;
    ctx.stroke();
    // chute + coin slot
    rounded(ctx, 58, TH - 40, 84, 18, 9);
    ctx.fillStyle = ink;
    ctx.fill();
    rounded(ctx, 156, TH - 40, 10, 18, 4);
    ctx.fill();
    scene.textures.addCanvas(BODY, c);
  }
  // awning: stripes with a scalloped edge (its own image: a covered machine lifts it to peek out)
  {
    const [c, ctx] = canvas(TW, TH);
    ctx.lineJoin = 'round';
    const aw = ART.awning;
    const bumps = 6;
    const bw = aw.w / bumps;
    const awning = () => {
      ctx.beginPath();
      ctx.moveTo(aw.x + 10, aw.y);
      ctx.lineTo(aw.x + aw.w - 10, aw.y);
      ctx.lineTo(aw.x + aw.w, aw.y + aw.h - 10);
      for (let k = bumps - 1; k >= 0; k--) ctx.arc(aw.x + bw * k + bw / 2, aw.y + aw.h - 10, bw / 2, 0, Math.PI, false);
      ctx.closePath();
    };
    awning();
    ctx.fillStyle = '#ffffff';
    ctx.fill();
    ctx.save();
    ctx.clip();
    ctx.fillStyle = css(C.mustard);
    for (let k = 0; k < bumps; k += 2) ctx.fillRect(aw.x + bw * k, 0, bw, aw.h + 14);
    ctx.restore();
    ctx.lineWidth = 6;
    ctx.strokeStyle = ink;
    awning();
    ctx.stroke();
    scene.textures.addCanvas(AWNING, c);
  }
  // glass: glare over the item, drawn above it
  {
    const [c, ctx] = canvas(TW, TH);
    rounded(ctx, WIN.x, WIN.y, WIN.w, WIN.h, WIN.r);
    ctx.save();
    ctx.clip();
    ctx.fillStyle = 'rgba(255,255,255,0.32)';
    ctx.beginPath();
    ctx.moveTo(WIN.x + 18, WIN.y);
    ctx.lineTo(WIN.x + 58, WIN.y);
    ctx.lineTo(WIN.x + 14, WIN.y + WIN.h);
    ctx.lineTo(WIN.x - 26, WIN.y + WIN.h);
    ctx.fill();
    ctx.fillStyle = 'rgba(255,255,255,0.22)';
    ctx.beginPath();
    ctx.moveTo(WIN.x + 70, WIN.y);
    ctx.lineTo(WIN.x + 84, WIN.y);
    ctx.lineTo(WIN.x + 40, WIN.y + WIN.h);
    ctx.lineTo(WIN.x + 26, WIN.y + WIN.h);
    ctx.fill();
    ctx.restore();
    ctx.strokeStyle = css(C.ink);
    ctx.lineWidth = 4;
    rounded(ctx, WIN.x, WIN.y, WIN.w, WIN.h, WIN.r);
    ctx.stroke();
    scene.textures.addCanvas(GLASS, c);
  }
  // card: the next item's back, a blank silhouette (never its face)
  {
    const [c, ctx] = canvas(100, 100);
    rounded(ctx, 6, 6, 88, 88, 20);
    ctx.fillStyle = css(C.creamDeep);
    ctx.fill();
    ctx.strokeStyle = 'rgba(74,44,42,0.55)';
    ctx.lineWidth = 5;
    ctx.stroke();
    scene.textures.addCanvas(CARD, c);
  }
  // counter badge
  {
    const [c, ctx] = canvas(96, 64);
    rounded(ctx, 4, 4, 88, 56, 28);
    ctx.fillStyle = css(C.mustard);
    ctx.fill();
    ctx.strokeStyle = css(C.ink);
    ctx.lineWidth = 6;
    ctx.stroke();
    scene.textures.addCanvas(BADGE, c);
  }
}

export class MachineView {
  /** behind the item: body, awning and the next item's card */
  private readonly back: Phaser.GameObjects.Container;
  /** above the item: glass glare and the counter badge */
  private readonly front: Phaser.GameObjects.Container;
  private readonly body: Phaser.GameObjects.Image;
  private readonly awning: Phaser.GameObjects.Image;
  private readonly card: Phaser.GameObjects.Image;
  private readonly glass: Phaser.GameObjects.Image;
  private readonly badge: Phaser.GameObjects.Image;
  private readonly count: Phaser.GameObjects.Text;
  /** badge rest y (the covered peek raises it) */
  private readonly badgeY: number;
  /** clips an item rising into (or sinking out of) the window to the glass */
  private readonly clip: Phaser.Display.Masks.GeometryMask;
  private readonly clipShape: Phaser.GameObjects.Graphics;
  private readonly k: number;
  private readonly tile: number;
  /** edge of the shown item's TileView */
  readonly itemSize: number;
  covered = false;
  gone = false;
  left = -1;
  private shade = 1;

  constructor(
    private readonly scene: Phaser.Scene,
    readonly x: number,
    readonly y: number,
    tile: number,
    depth: number,
  ) {
    bake(scene);
    this.tile = tile;
    this.k = (tile * 1.0) / TW;
    const k = this.k;
    // the item art (drawn bare, 0.76 of its view, with some air around the shape) fills the window
    this.itemSize = WIN.h * k * 1.5;
    this.back = scene.add.container(x, y).setDepth(depth - 0.05);
    this.front = scene.add.container(x, y).setDepth(depth + 0.05);
    this.body = scene.add.image(0, 0, BODY).setScale(k);
    this.awning = scene.add.image(0, 0, AWNING).setScale(k);
    this.card = scene.add.image(0, 0, CARD).setDisplaySize(WIN.h * k * 0.68, WIN.h * k * 0.68).setAngle(8);
    const w = this.window();
    this.card.setPosition(w.x - this.x + WIN.w * k * 0.2, w.y - this.y - WIN.h * k * 0.08);
    this.back.add([this.body, this.awning, this.card]);
    this.glass = scene.add.image(0, 0, GLASS).setScale(k);
    this.badgeY = (-TH / 2 + 34) * k;
    this.badge = scene.add.image((TW / 2 - 18) * k, this.badgeY, BADGE).setScale(k * 0.95);
    this.count = scene.add
      .text(this.badge.x, this.badge.y + 1 * k, '', {
        fontFamily: FONT,
        fontSize: `${Math.max(11 * vp.u, Math.round(34 * k))}px`,
        fontStyle: '700',
        color: css(C.ink),
      })
      .setOrigin(0.5);
    this.front.add([this.glass, this.badge, this.count]);
    const r = itemClip(tile);
    this.clipShape = scene.make.graphics({}, false);
    this.clipShape.fillStyle(0xffffff).fillRect(x + r.x, y + r.y, r.w, r.h);
    this.clip = this.clipShape.createGeometryMask();
  }

  /** Centre of the window, where the top item sits (device px). */
  window(): { x: number; y: number } {
    return { x: this.x, y: this.y + (WIN.y + WIN.h / 2 - TH / 2) * this.k };
  }

  /** The item count on the badge; `animate` bumps it. */
  setLeft(n: number, animate: boolean): void {
    if (n === this.left) return;
    this.left = n;
    this.count.setText(counterText(n));
    this.card.setVisible(n > 1);
    const d = animate ? deco(160) : 0;
    if (!d) return;
    this.scene.tweens.killTweensOf([this.badge, this.count]);
    const s = this.badge.scale;
    this.count.setScale(1);
    this.scene.tweens.add({ targets: this.count, scale: 1.35, duration: d / 2, yoyo: true, ease: 'Sine.easeOut' });
    this.scene.tweens.add({ targets: this.badge, scale: s * 1.2, duration: d / 2, yoyo: true, ease: 'Sine.easeOut', onComplete: () => this.badge.setScale(s) });
  }

  /** A new top item rises into the window from the chute. */
  slideIn(v: TileView, animate: boolean): void {
    const w = this.window();
    const d = animate ? dur(200) : 0;
    this.scene.tweens.killTweensOf(v);
    if (!d) return void v.clearMask().setPosition(w.x, w.y).setScale(1).setAlpha(1);
    // clipped to the glass while it rises: nothing shows below the window
    v.setMask(this.clip);
    v.setPosition(w.x, w.y + WIN.h * this.k * 0.5).setScale(0.8).setAlpha(0);
    this.scene.tweens.add({ targets: v, y: w.y, scale: 1, alpha: 1, duration: d, delay: dur(60), ease: 'Back.easeOut', onComplete: () => v.clearMask() });
  }

  /** Undo put an item back on top: the one shown sinks back into the machine. */
  sink(v: TileView, animate: boolean): void {
    this.scene.tweens.killTweensOf(v);
    const d = animate ? dur(160) : 0;
    if (!d) return void v.destroy();
    const w = this.window();
    v.setMask(this.clip);
    this.scene.tweens.add({ targets: v, y: w.y + WIN.h * this.k * 0.4, scale: 0.8, alpha: 0, duration: d, ease: 'Cubic.easeIn', onComplete: () => v.destroy() });
  }

  /** Emptied: the machine folds down and is gone (`onGone` puffs at its spot). */
  collapse(animate: boolean, onGone: ((x: number, y: number) => void) | null): void {
    if (this.gone) return;
    this.gone = true;
    const parts = [this.back, this.front];
    this.scene.tweens.killTweensOf(parts);
    const d = animate ? dur(240) : 0;
    if (!d) return void parts.forEach((p) => p.setVisible(false));
    onGone?.(this.x, this.y);
    this.scene.tweens.add({
      targets: parts,
      scaleY: 0.15,
      scaleX: 1.12,
      y: this.y + (TH / 2) * this.k * 0.7,
      alpha: 0,
      delay: dur(80),
      duration: d,
      ease: 'Back.easeIn',
      onComplete: () => parts.forEach((p) => p.setVisible(false)),
    });
  }

  /** Undo of the last item: the machine is back. */
  restore(animate: boolean): void {
    if (!this.gone) return;
    this.gone = false;
    const parts = [this.back, this.front];
    this.scene.tweens.killTweensOf(parts);
    parts.forEach((p) => p.setVisible(true).setAlpha(1).setPosition(this.x, this.y));
    const d = animate ? dur(200) : 0;
    if (!d) return void parts.forEach((p) => p.setScale(1));
    parts.forEach((p) => p.setScale(1.1, 0.2));
    this.scene.tweens.add({ targets: parts, scaleX: 1, scaleY: 1, duration: d, ease: 'Back.easeOut' });
  }

  get shown(): boolean {
    return !this.gone && this.back.visible;
  }

  get counter(): string {
    return this.count.text;
  }

  setCovered(covered: boolean, animate: boolean): void {
    if (covered === this.covered) return;
    this.covered = covered;
    const to = covered ? 0 : 1;
    const d = animate ? dur(180) : 0;
    if (!d) {
      this.shade = to;
      return this.applyShade();
    }
    this.scene.tweens.addCounter({
      from: this.shade,
      to,
      duration: d,
      onUpdate: (tw) => {
        this.shade = tw.getValue() ?? to;
        this.applyShade();
      },
    });
  }

  private applyShade(): void {
    const v = Math.round(0x99 + (0xff - 0x99) * this.shade);
    const tint = this.shade >= 1 ? 0xffffff : this.shade <= 0 ? COVERED_TINT : (v << 16) | (v << 8) | v;
    for (const o of [this.body, this.awning, this.card, this.glass, this.badge, this.count]) o.setTint(tint);
    const lift = peekLift(this.shade, this.tile);
    this.awning.setY(-lift.awning);
    this.badge.setY(this.badgeY - lift.badge);
    this.count.setY(this.badgeY + this.k - lift.badge);
  }

  /** Soft "nope" for a tap on a covered machine: a short side-to-side shake (with its item). */
  nope(item: TileView | null): void {
    const d = dur(240);
    if (!d) return;
    const targets = [this.back, this.front, ...(item ? [item] : [])];
    const amp = 4 * vp.u;
    for (const t of targets) {
      const x0 = t === item ? this.window().x : this.x;
      this.scene.tweens.add({ targets: t, x: { from: x0 - amp, to: x0 + amp }, duration: d / 4, yoyo: true, repeat: 1, ease: 'Sine.easeInOut', onComplete: () => t.setX(x0) });
    }
  }

  /** Win sweep: fade with the leftover tiles. */
  fadeOut(): void {
    const parts = [this.back, this.front];
    this.scene.tweens.killTweensOf(parts);
    const d = dur(260);
    if (!d) return void parts.forEach((p) => p.setVisible(false));
    this.scene.tweens.add({ targets: parts, alpha: 0, duration: d, onComplete: () => parts.forEach((p) => p.setVisible(false)) });
  }
}
